import { useEffect, useRef, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAvailableFlows } from '../hooks/usePortal';
import { humanize, portal, type AvailableFlowEntry } from '../lib/portal';
import { track } from '../lib/analytics';

/**
 * Dashboard renewal/extension eligibility card (Asana 1216270221908628).
 * Renders ONLY when Globus's available-flows read reports at least one
 * eligible flow for the application — eligibility is owned entirely by
 * Globus; the portal reflects, never computes (AP-8 rule). Purely additive
 * surface: while loading, on error, or when nothing is eligible it renders
 * nothing rather than cluttering the dashboard.
 *
 * The availability fetch is gated by `couldBeEligible` (owned by
 * DashboardPage, derived from journey/stage data this card doesn't hold):
 * ineligible-by-construction applicants never issue the Hub round-trip.
 *
 * The CTA per eligible flow starts that flow for real:
 * POST /applications/:id/flows/:applicationType. `applicationId` is passed
 * straight through — it is the id availability was queried for, and the BFF
 * reuses it as the prior application (see ApplicationsController.createFlow,
 * which also documents open product question F15 about which application id
 * that should be once a program has completed). Nothing else is sent: the
 * applicant id, program and target season are all resolved server-side.
 *
 * No optimistic UI: a create mints a real application upstream, so the button
 * shows a plain pending state and the card only reports success once Globus
 * has confirmed. Desired end date and justification are NOT collected here —
 * they are workflow steps inside the newly created application (BOP's create
 * contract accepts neither), so the applicant continues through the normal
 * step flow once the dashboard re-resolves onto the new application.
 *
 * NO NAVIGATION, deliberately: the new application id is handed to `onCreated`
 * but nothing routes to it. There is no per-id application route —
 * ApplicationPage resolves the applicant's `active` application itself — so
 * "open the new application's step flow" is not a redirect this card can make.
 * The created flow is non-terminal, so invalidating the applications list is
 * what makes it `active`, and the dashboard/step pages follow. Revisit if
 * ApplicationPage ever accepts an application id.
 */

const FLOW_LABELS: Record<string, string> = {
  renewal: 'Program renewal',
  extension: 'Program extension',
};

/** Verb forms for the summary sentence ("eligible to renew or extend your
 *  program") — the noun labels above read ungrammatically mid-sentence. */
const FLOW_VERBS: Record<string, string> = {
  renewal: 'renew',
  extension: 'extend',
};

/** Unknown/new flow types from the Hub render humanized instead of as an
 *  empty <strong> (forward-compat with future applicationTypes). */
const flowLabel = (t: string) => FLOW_LABELS[t] ?? humanize(t);
const flowVerb = (t: string) => FLOW_VERBS[t] ?? humanize(t).toLowerCase();

/** Pin to UTC so the rendered date matches the Hub's date in every timezone
 *  (same convention as CheckInsPage's fmtDate). */
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { timeZone: 'UTC' });

/** Only renewal/extension are creatable upstream — BOP's typed create enum.
 *  A forward-compat unknown flow type still renders (see flowLabel) but gets
 *  no button, because the create would be rejected by the Hub's enum. */
const isCreatable = (t: string): t is 'renewal' | 'extension' => t === 'renewal' || t === 'extension';

export default function RenewalEligibilityCard({
  applicationId,
  programName,
  couldBeEligible,
  onCreated,
}: {
  applicationId: string | number;
  programName?: string;
  /** Pre-fetch gate owned by the caller (DashboardPage): when false the
   *  availability read is never issued — see useAvailableFlows. */
  couldBeEligible: boolean;
  /** Notified after Globus confirms the new application, with its id. Cache
   *  refresh is owned by this card (see start()) — a caller must NOT refetch
   *  the applications list in here, or every create double-fetches. */
  onCreated?: (applicationId: string) => void;
}) {
  const { data, error, loading } = useAvailableFlows(applicationId, couldBeEligible);
  const queryClient = useQueryClient();
  /** The flow type currently being submitted (one at a time), or undefined. */
  const [busy, setBusy] = useState<string>();
  const [failure, setFailure] = useState<string>();
  /** The flow type Globus confirmed, or undefined — drives the success state. */
  const [created, setCreated] = useState<string>();

  // Observability: the card renders nothing on error BY DESIGN, so a broken
  // availability read would otherwise be invisible — emit one analytics event
  // per mount so the failure rate is measurable (api() throws plain Errors
  // without a status, so the message is the only upstream signal we have).
  const failureTracked = useRef(false);
  useEffect(() => {
    if (!error || failureTracked.current) return;
    failureTracked.current = true;
    track('available_flows_failed', { applicationId, error });
  }, [error, applicationId]);

  const start = async (applicationType: 'renewal' | 'extension') => {
    setBusy(applicationType);
    setFailure(undefined);
    try {
      const result = await portal.createFlowApplication(applicationId, applicationType);
      track('flow_application_created', { applicationId, applicationType });
      // Say so explicitly instead of letting the card silently vanish: it only
      // unmounts once `active` flips to the new application, and BOP's portal
      // availability read has no in-flight check, so the refetched flows can
      // still report "eligible" in that gap. The success state replaces the
      // CTAs, so a second create cannot be fired into it.
      setCreated(applicationType);
      // The applicant now holds a new application: the list and this card's
      // availability are both stale. Invalidate rather than hand-patch — and
      // this is the ONLY refresh: ['applications'] is the same shared query
      // useApplications observes, so a caller reloading in onCreated too would
      // just fetch the list twice.
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
      await queryClient.invalidateQueries({ queryKey: ['availableFlows', String(applicationId)] });
      onCreated?.(result.applicationId);
    } catch (e) {
      // Globus rejection envelopes (FLOW_WINDOW_CLOSED, APPLICANT_INELIGIBLE,
      // …) and the BFF's own ineligible/no-target-season conflicts arrive as
      // the Error message — render verbatim (AP-1), never a generic string.
      setFailure((e as Error).message);
      track('flow_application_rejected', { applicationId, applicationType, error: (e as Error).message });
    } finally {
      setBusy(undefined);
    }
  };

  const eligible = (data?.flows ?? []).filter((f: AvailableFlowEntry) => f.eligible);
  if (loading || error || eligible.length === 0) return null;

  return (
    <div className="card actioncard" data-testid="renewal-eligibility-card">
      <div className="actioncard-head">
        <span className="actioncard-icon" aria-hidden><CalendarClock size={20} /></span>
        <h2 style={{ margin: 0 }}>Renewal &amp; extension — {programName ?? 'your program'}</h2>
      </div>
      <p style={{ margin: 0, color: 'var(--ink-2)' }}>
        Good news — you're eligible to {eligible.map((f) => flowVerb(f.applicationType)).join(' or ')} your
        program.
      </p>
      <ul className="feed">
        {eligible.map((f, i) => (
          // Compound key: Globus may one day report the same applicationType
          // across several seasons — the index keeps keys collision-free.
          <li key={`${f.applicationType}-${i}`}>
            <strong>{flowLabel(f.applicationType)}</strong> <span className="pill ok">Eligible</span>
            {f.eligibleSince && <span className="globus-msg"> since {fmtDate(f.eligibleSince)}</span>}
          </li>
        ))}
      </ul>
      {failure && (
        <p className="globus-msg" role="alert" data-testid="renewal-flow-error">
          {failure}
        </p>
      )}
      {created ? (
        <p className="globus-msg" role="status" data-testid="renewal-flow-success">
          Your {flowLabel(created).toLowerCase()} application has been created — it is now your active program
          journey.
        </p>
      ) : (
        eligible.filter((f) => isCreatable(f.applicationType)).map((f) => (
          <button
            key={f.applicationType}
            type="button"
            className="btn-outline"
            // EVERY CTA disables while any create is in flight (busy is the
            // clicked type, not a per-button flag): one create at a time, so a
            // second flow only becomes clickable once the first settles.
            disabled={busy !== undefined}
            aria-busy={busy === f.applicationType}
            data-testid={`start-${f.applicationType}`}
            onClick={() => void start(f.applicationType as 'renewal' | 'extension')}
          >
            {busy === f.applicationType ? 'Starting…' : `Start ${flowLabel(f.applicationType).toLowerCase()}`}
          </button>
        ))
      )}
    </div>
  );
}
